import { createPortal } from "react-dom";
import { Hexagon, Rocket, Wand2, Zap, ArrowRight, Loader2 } from "lucide-react";
import { useUser } from "@clerk/react";
import { usePiecanLink } from "@/hooks/use-piecan-link";
import { trackPiecanHandoffClick } from "@/services/analytics";

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Cost of the generation the user just tried to run. */
  creditCost: number;
  /** The user's current balance. */
  credits: number;
}

const PERKS = [
  { Icon: Rocket, title: "Latest AI models", desc: "Our newest, most capable generators" },
  { Icon: Wand2, title: "All tools & styles", desc: "Characters, animations & edits" },
  { Icon: Zap, title: "Instant access", desc: "Your premium access carries over" },
];

/**
 * Out-of-credits moment. AnimePup purchases are temporarily unavailable, so
 * instead of sending the user to checkout we hand them off to PieCan — carrying
 * a tier-1 grant token when they're an eligible purchaser, or a plain link.
 */
export function UpsellModal({ isOpen, onClose, creditCost, credits }: UpsellModalProps) {
  const { user } = useUser();
  const { data, isLoading } = usePiecanLink(isOpen ? user?.id : null);

  if (!isOpen) return null;

  const url = data?.url;
  const eligible = !!data?.eligible && !!url;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} style={{ zIndex: 0 }} />
      <div
        className="relative rounded-3xl max-w-sm w-full mx-4 overflow-hidden shadow-2xl shadow-black/40 border border-[#d4a574]/15 animate-modal-in"
        style={{ zIndex: 1, background: "linear-gradient(170deg, #2a2035 0%, #1a1520 50%, #1a1520 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-6 pt-7 pb-4 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] bg-[#d4a574]/8 rounded-full blur-[60px] pointer-events-none" />
          <div className="relative">
            <div className="size-14 rounded-2xl bg-[#d4a574]/10 border border-[#d4a574]/20 flex items-center justify-center mx-auto mb-3">
              <Hexagon className="size-7 text-[#d4a574]" />
            </div>
            <h3 className="text-xl font-extrabold text-foreground mb-1">You're out of gizmos</h3>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              This costs <strong className="text-foreground">{creditCost}</strong> gizmos and you have{" "}
              <strong className="text-foreground">{credits}</strong>.{" "}
              {isLoading
                ? "One moment…"
                : eligible
                  ? "Purchases are temporarily unavailable on AnimePup — but you can keep creating with our latest AI models on PieCan."
                  : "Purchases are temporarily unavailable right now. Please check back soon."}
            </p>
          </div>
        </div>

        {eligible && (
          <div className="px-5 pb-5 flex flex-col gap-2.5">
            {PERKS.map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5">
                <div className="size-9 shrink-0 rounded-xl bg-[#d4a574]/10 border border-[#d4a574]/20 flex items-center justify-center">
                  <Icon className="size-[18px] text-[#d4a574]" strokeWidth={2.4} />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-extrabold text-foreground leading-tight">{title}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="px-5 pb-6 flex flex-col gap-2.5">
          {(isLoading || eligible) && (
            <a
              href={url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!url}
              onClick={(e) => {
                if (!url) {
                  e.preventDefault();
                  return;
                }
                trackPiecanHandoffClick(user?.id, "out_of_credits");
                onClose();
              }}
              className={`w-full py-3.5 rounded-2xl bg-[#d4a574] text-[#1a1520] text-[15px] font-extrabold hover:bg-[#dcb488] transition-all flex items-center justify-center gap-2 ${
                !url ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              {isLoading || !url ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <>
                  Continue on PieCan <ArrowRight className="size-4" />
                </>
              )}
            </a>
          )}
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-[12.5px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {eligible ? "Maybe later" : "Close"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
